import MarketingNav from "@/components/Layout/MarketingNav";
import MarketingFooter from "@/components/Layout/MarketingFooter";
import { useTheme } from "@/lib/theme";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight, Sparkles, Briefcase, Users, ShoppingBag,
  Truck, Camera, Cpu, Rocket, Building2, Check,
} from "lucide-react";

/* Iter R138m · Solutions page expansion — deeper persona narratives, outcome
   metrics, real-team photography, migration walk-through, MSME-tailored copy. */

const IMG = {
  services:  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80&auto=format&fit=crop",
  agency:    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80&auto=format&fit=crop",
  retail:    "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80&auto=format&fit=crop",
  founder:   "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80&auto=format&fit=crop",
};

const PERSONAS = [
  {
    icon: Briefcase,
    t: "Service & consulting firms",
    d: "Track every engagement from kickoff to invoice. Utilisation, deliverables and client comms in one thread — not scattered across three inboxes.",
    chip: "PM + CRM + Finance",
    outcomes: ["Cut billable-leak by chasing timesheets in-app", "Client portal replaces the shared Drive folder", "AI risk digest catches slipping engagements early"],
  },
  {
    icon: Camera,
    t: "Creative & digital agencies",
    d: "Run client work, internal hiring and new-business pipeline side by side. White-label the client portal, brand the invoices, keep the margin.",
    chip: "PM + HRMS + CRM",
    outcomes: ["Per-workspace branding on every touchpoint", "Freelancer & FTE utilisation on one screen", "Recognition & appraisals baked into HR"],
  },
  {
    icon: ShoppingBag,
    t: "D2C brands & retailers",
    d: "Plan launches, coordinate suppliers, run store-ops and marketing calendars — without buying three more SaaS subscriptions.",
    chip: "Ops + CRM + PM",
    outcomes: ["One roadmap across marketing, ops and supply", "Vendor comms tracked, not lost in WhatsApp", "Approval flows for pricing, creatives and PRs"],
  },
  {
    icon: Truck,
    t: "Trading & distribution",
    d: "Leads, quotations, orders and collections — modelled the way Indian MSMEs actually transact. Multi-location, multi-currency, GST-aware.",
    chip: "CRM end-to-end",
    outcomes: ["Quotation → order → invoice without re-keying", "Ageing receivables & dunning automations", "Exporter workflows with account-level rollups"],
  },
  {
    icon: Cpu,
    t: "Tech & product teams",
    d: "Roadmaps, sprints and people-ops in one place. Standardise how engineering, design and customer success hand off work between teams.",
    chip: "PM + HRMS",
    outcomes: ["Subtask rollups keep sprints honest", "AI-drafted release notes & retros", "Onboarding paths per role, not per hire"],
  },
  {
    icon: Camera,
    t: "Studios & production houses",
    d: "Bring producers, freelancers and clients into the same workspace. Approvals, schedules and project margins on one screen.",
    chip: "PM + Ops + Finance",
    outcomes: ["Shot-list to invoice, all traceable", "Reviewer links keep clients out of your Slack", "Rate-cards & margins visible per project"],
  },
  {
    icon: Rocket,
    t: "Founders & operators",
    d: "Run the whole business — projects, hiring, sales and process — from one dashboard. Stop being the human integration layer between five tools.",
    chip: "All-in-one",
    outcomes: ["A single Monday-morning briefing", "See burn, pipeline and delivery in one glance", "Delegate with visible ownership, not spreadsheets"],
  },
  {
    icon: Building2,
    t: "Established MSMEs going digital",
    d: "Replace the patchwork of Excel, WhatsApp and disconnected SaaS with a single system of record built for MSME scale — not enterprise complexity.",
    chip: "Run · Grow · Scale",
    outcomes: ["A phased switch-on, not a big-bang rollout", "Indian-first billing, GST, and localisation", "No consultant army required"],
  },
];

const MIGRATION_STEPS = [
  { n: "01", t: "Import what you have", d: "Bulk import leads, employees, projects and clients from CSV/XLSX in minutes. Async background processing so 10,000-row files never time out." },
  { n: "02", t: "Turn on your first pillar", d: "Pick the pillar with the loudest pain — usually PM or CRM — and run it real for two weeks while the rest of the toolkit stays put." },
  { n: "03", t: "Layer in the next module", d: "HR, sales outreach, comms, finance — turn each one on when the team is ready. No forced migration. No downtime." },
  { n: "04", t: "Retire the old tools", d: "Most customers shut down 3–5 subscriptions in the first quarter. The savings usually cover the license fee." },
];

export default function Solutions() {
  const { branding } = useTheme();
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="label-eyebrow mb-4">Who it&apos;s for</div>
            <h1 className="heading-display text-5xl sm:text-7xl mb-6 max-w-4xl">
              One workspace. Many ways to <span className="text-primary">grow a business.</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl">
              {branding.platform_name} flexes to the shape of your team. Whether you&apos;re 12 people or 250,
              you choose the modules that matter today — and switch on the rest when your business asks for them.
            </p>
          </div>
          <div className="lg:col-span-4 hidden lg:block">
            <img
              src={IMG.services}
              alt="A modern MSME team collaborating"
              loading="lazy"
              className="w-full aspect-[4/5] object-cover border border-border grayscale hover:grayscale-0 transition-all duration-700"
            />
          </div>
        </div>
      </section>

      {/* PERSONA CARDS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid md:grid-cols-2 gap-3">
          {PERSONAS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="border border-border p-8 hover:border-primary/40 transition flex flex-col gap-4" data-testid={`solutions-card-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Icon className="w-5 h-5 text-primary mt-1 shrink-0" />
                    <h3 className="font-bold text-2xl" style={{ fontFamily: "var(--font-heading)" }}>{p.t}</h3>
                  </div>
                  <span className="chip text-[10px] chip-internal whitespace-nowrap" data-testid={`solutions-card-${i}-chip`}>{p.chip}</span>
                </div>
                <p className="text-muted-foreground">{p.d}</p>
                <ul className="space-y-2 mt-2 text-sm">
                  {p.outcomes.map((o, oi) => (
                    <li key={oi} className="flex gap-2 items-start text-foreground/80">
                      <Check className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* INTERDEPARTMENT COLLABORATION IMAGE BAND */}
      <section className="border-y border-border/50 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <img
              src={IMG.agency}
              alt="Cross-functional MSME team at work"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover border border-border grayscale hover:grayscale-0 transition-all duration-700"
              data-testid="solutions-hero-img"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="label-eyebrow mb-4"><Users className="inline w-3 h-3 mr-1" /> Interdepartmental by design</div>
            <h2 className="heading-display text-4xl sm:text-5xl mb-6">
              Sales, delivery, finance and support — on one thread.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-6">
              MSMEs don&rsquo;t have the luxury of siloed teams. {branding.platform_name} is built so a deal closed
              in CRM becomes a project in PM, timesheets flow into invoices, and support tickets stay visible
              to the account manager who owns the relationship.
            </p>
            <ul className="space-y-3 text-sm">
              {[
                "No re-keying data between departments — ever.",
                "Roles ensure people see what they need, and nothing they don't.",
                "AI digests keep each department briefed without meetings.",
                "Audit trails make handoffs transparent, not accusatory.",
              ].map((line, i) => (
                <li key={i} className="flex gap-2 items-start text-foreground/85">
                  <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* MIGRATION PATH */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="mb-12">
          <div className="label-eyebrow mb-4">How to switch</div>
          <h2 className="heading-display text-4xl sm:text-5xl max-w-3xl">
            You don&apos;t migrate. You <span className="text-primary">phase in.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-4 gap-px bg-border">
          {MIGRATION_STEPS.map((s, i) => (
            <div key={i} className="bg-background p-8" data-testid={`migration-step-${s.n}`}>
              <div className="label-eyebrow mb-3">{s.n}</div>
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: "var(--font-heading)" }}>{s.t}</h3>
              <p className="text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border/50 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow mb-4">Not sure which to start with?</div>
            <h2 className="heading-display text-3xl sm:text-5xl">
              Start where the pain is loudest.
            </h2>
          </div>
          <div className="space-y-4">
            <p className="text-muted-foreground text-base">
              Most teams pick one pillar — usually projects or sales — and layer the rest in over the next quarter.
              You don&apos;t have to migrate everything on day one. You just have to start.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/signup">
                <Button size="lg" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="solutions-cta-start">
                  Start free <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/features">
                <Button size="lg" variant="outline" className="rounded-none h-12 px-6 text-sm font-bold tracking-wide uppercase" data-testid="solutions-cta-features">
                  Explore features <Sparkles className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
